import * as XLSX from 'xlsx';

import type { ProcessoSection } from '@/modules/reports/presentation/pages/hooks/useProcessosSections';

/** Alvos no formulário que aceitam importação de uma única coluna de número de processo (texto). */
export type ExcelImportNumeroAlvo =
  | { tipo: 'processos'; section: ProcessoSection }
  | { tipo: 'honorarios-sentenca' }
  | { tipo: 'honorarios-cumprimento' }
  | { tipo: 'oficios-requisitorios' }
  | { tipo: 'oficios-demais' };

export type LinhaPreviewImportNumeroProcesso = {
  /** Número da linha na planilha (1 = cabeçalho). */
  indicePlanilha: number;
  /** Valor importado como texto (trim aplicado na confirmação). */
  numeroProcesso: string;
  valido: boolean;
  erros: string[];
};

function normalizarCabecalho(c: string): string {
  return c
    .replace(/\u00A0/g, ' ')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Converte célula do Excel para string, preservando o que for possível como texto. */
export function celulaExcelComoTexto(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'string') return v.replace(/\u00A0/g, ' ').trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return String(v).replace(/\u00A0/g, ' ').trim();
}

function encontrarChaveNumeroProcesso(primeiraLinha: Record<string, unknown>): string | null {
  const keysOrig = Object.keys(primeiraLinha);
  if (keysOrig.length === 0) return null;

  if (keysOrig.length === 1) {
    return keysOrig[0];
  }

  type Scored = { orig: string; score: number };
  const scored: Scored[] = keysOrig.map((orig) => {
    const norm = normalizarCabecalho(orig);
    let score = 0;
    if (norm.includes('numero') || norm.includes('numer')) score += 2;
    if (norm.includes('processo')) score += 2;
    if (norm.includes('nr') && norm.includes('proc')) score += 2;
    if (norm.includes('nº') || norm.includes('n.')) score += 1;
    return { orig, score };
  });

  scored.sort((a, b) => b.score - a.score);
  if (scored[0].score >= 2) {
    return scored[0].orig;
  }

  const fallback = keysOrig.find((k) => {
    const n = normalizarCabecalho(k);
    return n.includes('numero') || n.includes('numer');
  });
  return fallback ?? null;
}

/**
 * Lê a primeira aba com uma coluna de cabeçalho identificável como número do processo.
 * Valores são tratados como texto. Linhas vazias são ignoradas.
 * Números repetidos (na planilha ou já na lista) são permitidos.
 */
export function parseNumeroProcessoExcel(
  arrayBuffer: ArrayBuffer,
): { linhas: LinhaPreviewImportNumeroProcesso[]; erroCabeçalho: string | null } {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false });

  if (rows.length === 0) {
    return { linhas: [], erroCabeçalho: 'A planilha está vazia.' };
  }

  const chave = encontrarChaveNumeroProcesso(rows[0]);
  if (!chave) {
    return {
      linhas: [],
      erroCabeçalho:
        'Não foi possível identificar a coluna do número do processo. Use um cabeçalho como "Número do processo" na primeira linha.',
    };
  }

  const linhas: LinhaPreviewImportNumeroProcesso[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const numeroProcesso = celulaExcelComoTexto(row[chave]);
    if (!numeroProcesso) {
      continue;
    }

    linhas.push({
      indicePlanilha: i + 2,
      numeroProcesso,
      valido: true,
      erros: [],
    });
  }

  return { linhas, erroCabeçalho: null };
}

/** Monta itens com id/data para qualquer lista que use só número de processo. */
export function montarItensNumeroProcessoImportados(linhas: LinhaPreviewImportNumeroProcesso[]): Array<{
  id: string;
  numeroProcesso: string;
  dataAdicao: Date;
}> {
  const agora = new Date();
  return linhas
    .filter((l) => l.valido)
    .map((l, i) => ({
      id: `import-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
      numeroProcesso: l.numeroProcesso.trim(),
      dataAdicao: agora,
    }));
}
